import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaTriangleExclamation, FaLock } from 'react-icons/fa6';
import { addToast } from '@/store/app/action';
import { PrimaryButton, SecondaryButton } from '@/components/Buttons';
import Loader from '@/components/Loader';
import {
  getSftpConfig,
  createSftpCredentials,
  updateSftpConfig,
  getInventoryOutputFolders,
} from '@/api/sftp';

// Our own SFTP host. A store pointed at it is pushing files to us, so its
// connection details are ours to issue, not theirs to edit. Any other host means
// we are reading from their server and the details are editable.
const SAUCEY_SFTP_HOST = 'sftp.sauceyteam.com';

const isSauceyHosted = (host) => !!host && String(host).indexOf(SAUCEY_SFTP_HOST) === 0;

// Header names in the retailer's file, mapped onto our canonical columns —
// the same shape the CDC column mapping uses.
const COLUMN_FIELDS = [
  { key: 'sku', label: 'Retailer SKU' },
  { key: 'price', label: 'Price' },
  { key: 'quantity', label: 'Quantity' },
  { key: 'upc', label: 'UPC' },
  { key: 'title', label: 'Product name' },
  { key: 'category', label: 'Category' },
  { key: 'last_sold', label: 'Last sold' },
  { key: 'discontinued', label: 'Discontinued' },
];

const CONNECTION_FIELDS = [
  { key: 'host', label: 'Host' },
  { key: 'user', label: 'User' },
  { key: 'password', label: 'Password' },
  { key: 'path', label: 'Path' },
];

const COOLDOWN_UNITS = ['days', 'hours', 'minutes'];

const errorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  if (data?.message) return data.message;
  if (data?.error) return data.error;
  return error?.message || fallback;
};

const Field = ({ label, children, hint }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
      {label}
    </label>
    {children}
    {hint && <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{hint}</p>}
  </div>
);

const textClass =
  'w-full px-3 py-2 font-mono text-sm border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 disabled:bg-slate-100 dark:disabled:bg-slate-800 disabled:text-slate-500';

/**
 * A store's SFTP feed configuration.
 *
 * Create and update are both wired, mirroring stockroom: a store with no
 * credentials gets a provisioning action, one that has them gets the full form.
 *
 * The save path is deliberately gated on having loaded the current settings
 * first. `PATCH .../ftp-config` replaces `import_settings` wholesale rather than
 * merging, so writing a form that was seeded from defaults would delete the
 * store's host, user and password along with everything else.
 */
const SftpSettings = ({ storeId, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.app.user);
  const isSauceyAdmin = user?.type === 'saucey_admin';

  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null); // complete import_settings, or null
  const [readable, setReadable] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [outputPaths, setOutputPaths] = useState([]);
  const [outputError, setOutputError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSftpConfig(storeId);
      // The handler responds with `seller.import_settings` itself — a bare
      // settings object, or an empty body for a store that has none. The
      // `??` keeps a wrapped envelope working if the endpoint grows one.
      const payload = res && typeof res === 'object' && !Array.isArray(res) ? res : null;
      setSettings(payload?.import_settings ?? payload);
      setReadable(true);
      setLoadError(null);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404 || status === 405 || status === 501) {
        // The read endpoint isn't deployed. Not an error the operator caused.
        setReadable(false);
        setLoadError(null);
      } else {
        setReadable(false);
        setLoadError(errorMessage(err, 'Could not load the SFTP configuration.'));
      }
      setSettings(null);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    getInventoryOutputFolders()
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        setOutputPaths(list.map((f) => f?.Prefix).filter(Boolean));
      })
      .catch((err) => {
        setOutputPaths([]);
        setOutputError(errorMessage(err, 'Could not list the processed output folders.'));
      });
  }, []);

  const credentials = settings?.credentials || {};
  const currentPath = credentials.processed_output_path || '';
  // A <select> whose value matches no <option> renders as the first option, so a
  // stored path missing from the bucket listing would read as "Not set" — and
  // saving from that state would clear it. Always carry the current value.
  const currentPathMissing = !!currentPath && !outputPaths.includes(currentPath);
  const outputOptions = currentPathMissing ? [currentPath, ...outputPaths] : outputPaths;
  const hasCredentials = !!credentials.host;
  const connectionEditable = hasCredentials && !isSauceyHosted(credentials.host);

  const setSetting = (key, value) => setSettings((prev) => ({ ...(prev || {}), [key]: value }));

  const setCredential = (key, value) =>
    setSettings((prev) => ({
      ...(prev || {}),
      credentials: { ...((prev || {}).credentials || {}), [key]: value },
    }));

  const setCooldown = (unit, value) =>
    setSettings((prev) => ({
      ...(prev || {}),
      default_cooldown: { ...((prev || {}).default_cooldown || {}), [unit]: value },
    }));

  const handleCreate = async () => {
    setBusy(true);
    setError(null);
    try {
      const seller = await createSftpCredentials(storeId);
      const next = seller?.import_settings;
      if (next) {
        setSettings(next);
        setReadable(true);
      } else {
        await load();
      }
      dispatch(addToast({ type: 'success', message: 'SFTP credentials created', timer: 5 }));
      if (onSaved) await onSaved();
    } catch (err) {
      setError(errorMessage(err, 'Failed to create SFTP credentials.'));
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    if (!settings) return;
    setBusy(true);
    setError(null);
    try {
      // Send the settings back whole — the server replaces the subdocument.
      const seller = await updateSftpConfig(storeId, settings);
      if (seller?.import_settings) setSettings(seller.import_settings);
      dispatch(addToast({ type: 'success', message: 'SFTP settings saved', timer: 5 }));
      if (onSaved) await onSaved();
      onClose();
    } catch (err) {
      setError(errorMessage(err, 'Failed to save SFTP settings.'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-10">
        <Loader />
        <p className="mt-3 text-slate-600 dark:text-slate-300">Loading SFTP configuration…</p>
      </div>
    );
  }

  // Cannot read the current settings: offer provisioning (which takes no body
  // and so is safe) but never a save, which would wipe what is there.
  if (!readable) {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
          <FaTriangleExclamation className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />
          <div className="text-sm text-amber-800 dark:text-amber-200">
            {loadError ? (
              <p>{loadError}</p>
            ) : (
              <>
                <p className="font-medium">Current SFTP settings can&rsquo;t be read yet.</p>
                <p className="mt-1">
                  There is no merchant-scoped endpoint that returns{' '}
                  <code className="font-mono">import_settings</code> — the store endpoint drops
                  it. Editing is disabled rather than risking a save, because the update
                  replaces the whole settings object and would delete this store&rsquo;s host,
                  user and password.
                </p>
              </>
            )}
          </div>
        </div>

        {isSauceyAdmin ? (
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
            <p className="text-sm text-slate-600 dark:text-slate-300">
              You can still provision credentials for a store that has none. This is safe —
              it sends no settings.
            </p>
            <div className="mt-3">
              <PrimaryButton onClick={handleCreate} disabled={busy} loading={busy}>
                Create SFTP credentials
              </PrimaryButton>
            </div>
          </div>
        ) : null}

        {error && (
          <div className="rounded-lg border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20 p-3">
            <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p>
          </div>
        )}

        <div className="flex justify-end">
          <SecondaryButton onClick={onClose}>Close</SecondaryButton>
        </div>
      </div>
    );
  }

  if (!hasCredentials) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          This store has no SFTP credentials yet. Creating them provisions an account on
          Saucey&rsquo;s SFTP host that the store pushes inventory files to.
        </p>

        {!isSauceyAdmin && (
          <div className="flex items-start gap-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-3">
            <FaLock className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-400" />
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Only Saucey admins can provision SFTP credentials. Ask your account manager to
              set this store up.
            </p>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20 p-3">
            <p className="text-sm text-rose-700 dark:text-rose-300">{error}</p>
          </div>
        )}

        <div className="flex justify-end gap-3">
          <SecondaryButton onClick={onClose} disabled={busy}>
            Cancel
          </SecondaryButton>
          {isSauceyAdmin && (
            <PrimaryButton onClick={handleCreate} disabled={busy} loading={busy}>
              Create SFTP credentials
            </PrimaryButton>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100">
            {connectionEditable ? 'Read from external SFTP' : 'Push to Saucey SFTP'}
          </h3>
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {connectionEditable
            ? 'We connect out to this server and pull the inventory file.'
            : 'These are the details this store uses to push files to us. They are issued by Saucey and not editable here.'}
        </p>

        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {CONNECTION_FIELDS.map((field) => (
            <Field key={field.key} label={field.label}>
              <input
                type="text"
                value={credentials[field.key] ?? ''}
                disabled={!connectionEditable}
                onChange={(e) => setCredential(field.key, e.target.value)}
                spellCheck={false}
                autoComplete="off"
                className={textClass}
              />
            </Field>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-1">
          Column mapping
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 mb-3">
          The header name in the retailer&rsquo;s file that feeds each column. Leave blank for
          anything the file doesn&rsquo;t carry.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {COLUMN_FIELDS.map((field) => (
            <Field key={field.key} label={field.label}>
              <input
                type="text"
                value={settings?.[field.key] ?? ''}
                onChange={(e) => setSetting(field.key, e.target.value)}
                spellCheck={false}
                className={textClass}
              />
            </Field>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-3">
          Import behaviour
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field
            label="Quantity threshold"
            hint="Items at or below this count are treated as out of stock."
          >
            <input
              type="number"
              value={settings?.quantity_threshold ?? ''}
              onChange={(e) =>
                setSetting(
                  'quantity_threshold',
                  e.target.value === '' ? '' : parseFloat(e.target.value)
                )
              }
              className={textClass}
            />
          </Field>

          {COOLDOWN_UNITS.map((unit) => (
            <Field key={unit} label={`Inventory cooldown ${unit}`}>
              <input
                type="number"
                value={settings?.default_cooldown?.[unit] ?? ''}
                onChange={(e) =>
                  setCooldown(unit, e.target.value === '' ? '' : parseFloat(e.target.value))
                }
                className={textClass}
              />
            </Field>
          ))}
        </div>

        <div className="mt-4 space-y-2">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!settings?.sync_upc}
              onChange={(e) => setSetting('sync_upc', e.target.checked)}
              className="mt-0.5 form-checkbox"
            />
            <span className="text-sm text-slate-700 dark:text-slate-300">Sync UPC</span>
          </label>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!settings?.disable_unmatched}
              onChange={(e) => setSetting('disable_unmatched', e.target.checked)}
              className="mt-0.5 form-checkbox"
            />
            <span className="text-sm text-slate-700 dark:text-slate-300">
              Disable unmatched items
            </span>
          </label>
        </div>
      </div>

      <div>
        <Field
          label="Processed output path"
          hint="Where processed files are written. Setting this clears any custom delimiter, since processed output is always CSV."
        >
          <div className="flex gap-2">
            <select
              value={credentials.processed_output_path ?? ''}
              onChange={(e) => setCredential('processed_output_path', e.target.value || undefined)}
              className="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            >
              <option value="">Not set</option>
              {outputOptions.map((path) => (
                <option key={path} value={path}>
                  {path}
                  {currentPathMissing && path === credentials.processed_output_path
                    ? '  (current — not in bucket listing)'
                    : ''}
                </option>
              ))}
            </select>
            {currentPath && (
              <button
                type="button"
                onClick={() => setCredential('processed_output_path', undefined)}
                className="px-3 py-2 text-sm text-slate-600 dark:text-slate-300 border border-slate-300 dark:border-slate-600 rounded-md hover:bg-slate-50 dark:hover:bg-slate-700"
              >
                Clear
              </button>
            )}
          </div>
          {outputError && (
            <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
              {outputError} Existing value is preserved.
            </p>
          )}
          {!outputError && outputPaths.length === 0 && (
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              The processed bucket listed no folders.
            </p>
          )}
        </Field>
      </div>

      {error && (
        <div className="rounded-lg border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-900/20 p-3">
          <p className="text-sm text-rose-700 dark:text-rose-300 whitespace-pre-wrap break-words">
            {error}
          </p>
        </div>
      )}

      <div className="flex justify-end gap-3">
        <SecondaryButton onClick={onClose} disabled={busy}>
          Cancel
        </SecondaryButton>
        <PrimaryButton onClick={handleSave} disabled={busy} loading={busy}>
          Save settings
        </PrimaryButton>
      </div>
    </div>
  );
};

export default SftpSettings;
